import React, { useState } from "react";
import { FiMail, FiTrash2, FiEye, FiUserPlus, FiCheckCircle, FiUser } from "react-icons/fi";

const statusOptions = [
  { value: "active", label: "Đang học" },
  { value: "paused", label: "Tạm nghỉ" },
  { value: "onleave", label: "Bảo lưu" },
  { value: "dropped", label: "Đã nghỉ" },
  { value: "graduated", label: "Hoàn thành" }
];

export default function TabStudents({
  students = [],
  classInfo,
  canEdit,
  allStudents = [],
  onAddStudent,
  onRemoveStudent,
  onUpdateStatus
}) {
  const [search, setSearch] = useState("");
  const [modalAdd, setModalAdd] = useState(false);
  const [modalStudent, setModalStudent] = useState(null);

  // Lọc theo tên/email
  const filtered = students.filter(
    (s) =>
      (s.firstName + " " + s.lastName)
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      (s.email || "").toLowerCase().includes(search.toLowerCase())
  );

  // Lấy các học viên chưa trong lớp để thêm mới
  const studentIdsInClass = students.map(s => s.id);
  const addableStudents = allStudents.filter(
    s => !studentIdsInClass.includes(s.id)
  );

  // Modal thêm học viên
  function AddStudentModal() {
    const [selected, setSelected] = useState(null);
    const [adding, setAdding] = useState(false);
    return !modalAdd ? null : (
      <div className="fixed z-50 inset-0 bg-black/30 flex items-center justify-center">
        <div className="bg-card rounded-2xl shadow max-w-md w-full p-6 relative border border-border animate-fadein">
          <button
            className="absolute right-3 top-3 text-xl text-text2 hover:text-error transition"
            onClick={() => setModalAdd(false)}
            aria-label="Đóng"
          >✕</button>
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <FiUserPlus className="text-primary" /> Thêm học viên vào lớp
          </h3>
          <select
            className="w-full border border-border rounded-2xl px-3 py-2 mb-4 text-base bg-card"
            value={selected?.id || ""}
            onChange={e => {
              const stu = addableStudents.find(s => s.id === e.target.value);
              setSelected(stu || null);
            }}
          >
            <option value="">-- Chọn học viên --</option>
            {addableStudents.map(s => (
              <option key={s.id} value={s.id}>
                {s.firstName} {s.lastName} ({s.email})
              </option>
            ))}
          </select>
           <div className="flex justify-end gap-2">
            <button
              className="px-4 py-2 rounded-2xl bg-bg hover:bg-border font-medium text-text2 border border-border transition"
              onClick={() => setModalAdd(false)}
              disabled={adding}
            >Huỷ</button>
            <button
              className={`px-4 py-2 rounded-2xl bg-primary text-text-button hover:brightness-95 flex items-center gap-2 font-semibold shadow transition ${adding ? "opacity-60 cursor-not-allowed" : ""}`}
              disabled={!selected || adding}
              onClick={async () => {
                setAdding(true);
                await onAddStudent(selected);
                setAdding(false);
                setModalAdd(false);
              }}
            >
              <FiCheckCircle className={adding ? "animate-spin" : ""} /> Thêm vào lớp
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Modal chi tiết học viên
  function StudentDetailModal() {
    const s = modalStudent;
    if (!s) return null;
    return (
      <div className="fixed z-50 inset-0 bg-black/30 flex items-center justify-center">
        <div className="bg-card rounded-2xl shadow max-w-lg w-full p-6 relative border border-border animate-fadein">
          <button
            className="absolute right-3 top-3 text-xl text-text2 hover:text-error"
            onClick={() => setModalStudent(null)}
            aria-label="Đóng"
          >✕</button>
          <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
            <FiUser className="text-primary" /> Chi tiết học viên
          </h3>
          <div className="space-y-2 text-[15px]">
            <div>
              <span className="font-semibold">Họ tên:</span> {s.firstName} {s.lastName}
            </div>
            <div>
              <span className="font-semibold">Email:</span>{" "}
              <a href={`mailto:${s.email}`} className="text-primary underline">{s.email}</a>
            </div>
            <div>
              <span className="font-semibold">SĐT:</span> {s.phoneNumber || "-"}
            </div>
            <div>
              <span className="font-semibold">Giới tính:</span> {s.gender === "male" ? "Nam" : s.gender === "female" ? "Nữ" : "-"}
            </div>
            <div>
              <span className="font-semibold">Ngày sinh:</span> {s.birthday || "-"}
            </div>
            <div>
              <span className="font-semibold">Trạng thái:</span>{" "}
              {statusOptions.find(opt => opt.value === s.status)?.label || "Không xác định"}
            </div>
            {s.note && (
              <div>
                <span className="font-semibold">Ghi chú:</span> <span className="italic">{s.note}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
        <input
          className="border border-border rounded-2xl px-3 py-2 w-full sm:w-72 text-base bg-card text-text"
          placeholder="Tìm kiếm học viên theo tên hoặc email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {canEdit && (
          <button
            className="flex items-center gap-2 px-3 py-2 bg-primary hover:brightness-95 text-text-button rounded-2xl font-medium ml-auto shadow transition"
            onClick={() => setModalAdd(true)}
          >
            <FiUserPlus /> Thêm học viên
          </button>
        )}
      </div>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="min-w-[760px] w-full text-[15px] border-none">
          <thead>
            <tr className="bg-bg text-menu">
              <th className="px-2 py-2 text-left">#</th>
              <th className="px-2 py-2 text-left">Họ tên</th>
              <th className="px-2 py-2 text-left">Email</th>
              <th className="px-2 py-2 text-left">SĐT</th>
              <th className="px-2 py-2">Giới tính</th>
              <th className="px-2 py-2">Ngày sinh</th>
              <th className="px-2 py-2">Trạng thái</th>
              <th className="px-2 py-2 text-left">Ghi chú</th>
              <th className="px-2 py-2 text-center">Hành động</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-8 text-center text-text2">
                  Không tìm thấy học viên nào.
                </td>
              </tr>
            ) : (
              filtered.map((s, idx) => (
                <tr key={s.id} className="border-b border-border hover:bg-primary/5 group">
                  <td className="px-2 py-2">{idx + 1}</td>
                  <td className="px-2 py-2 font-medium">{s.firstName} {s.lastName}</td>
                  <td className="px-2 py-2">
                    <a
                      href={`mailto:${s.email}`}
                      className="text-primary hover:underline flex items-center gap-1"
                      title="Gửi email"
                    >
                      <FiMail /> {s.email}
                    </a>
                  </td>
                  <td className="px-2 py-2">{s.phoneNumber || "-"}</td>
                  <td className="px-2 py-2">
                    {s.gender === "male"
                      ? "Nam"
                      : s.gender === "female"
                      ? "Nữ"
                      : "-"}
                  </td>
                  <td className="px-2 py-2">{s.birthday || "-"}</td>
                  <td className="px-2 py-2">
                    {canEdit ? (
                      <select
                        className="border border-border rounded-2xl px-2 py-1 bg-card text-text"
                        value={s.status || "active"}
                        onChange={e =>
                          onUpdateStatus(s.id, e.target.value)
                        }
                      >
                        {statusOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      statusOptions.find(opt => opt.value === s.status)?.label || "-"
                    )}
                  </td>
                  <td className="px-2 py-2">{s.note || ""}</td>
                  <td className="px-2 py-2 flex gap-2 justify-center">
                    <button
                      title="Xem chi tiết"
                      className="text-primary hover:text-menu rounded-full p-1 transition"
                      onClick={() => setModalStudent(s)}
                    >
                      <FiEye />
                    </button>
                    {canEdit && (
                      <button
                        title="Gỡ khỏi lớp"
                        className="text-error hover:text-red-700 rounded-full p-1 transition"
                        onClick={() => onRemoveStudent(s.id)}
                      >
                        <FiTrash2 />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {AddStudentModal()}
      {StudentDetailModal()}
    </div>
  );
}
